import { defaultClient } from '@/services/api.ts';
import { isDateRangeTooWide, MAX_BULK_DATE_RANGE_DAYS } from '@/utils/dateRange.ts';

// bb8-api caps roomIds at 200 per request (TooManyExternalIdsException); chunk defensively so a very
// large property still works instead of erroring the whole page.
const MAX_ROOM_IDS_PER_REQUEST = 200;

function chunk(roomIds: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < roomIds.length; i += size) {
    chunks.push(roomIds.slice(i, i + size));
  }
  return chunks;
}

// Callers should validate the range before ever reaching here (see Home.vue::updateDateRange) - this
// is a last-resort safety net so a caller that skips that check still fails fast instead of getting a
// 400 from bb8-api (or, for bookings_quantity/bulk, an unbounded query bb8-api doesn't reject at all).
function assertDateRangeWithinMax(startDate: string, endDate: string): void {
  if (isDateRangeTooWide(startDate, endDate)) {
    throw new Error(
      `Date range from ${startDate} to ${endDate} exceeds the maximum of ${MAX_BULK_DATE_RANGE_DAYS} days`,
    );
  }
}

async function fetchBulkChunk(path: string, roomIds: string[], params: Record<string, string>): Promise<any[]> {
  const query = new URLSearchParams({ ...params, roomIds: roomIds.join(',') });
  const basePath = defaultClient.basePath.replace(/\/+$/, '');

  const response = await fetch(`${basePath}${path}?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: defaultClient.authentications.apiKey.apiKey,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw response;
  }

  return response.json();
}

// The generated bb_8_api SDK (bb-8-sdk-js) doesn't have these bulk operations yet, so these are
// hand-written until it's regenerated - kept as thin wrappers matching the SDK's own conventions
// (same base URL, same Basic-auth header) rather than a general-purpose HTTP client.
async function fetchBulk(path: string, roomIds: string[], params: Record<string, string>): Promise<any[]> {
  if (!roomIds.length) {
    return [];
  }
  const results = await Promise.all(
    chunk(roomIds, MAX_ROOM_IDS_PER_REQUEST).map((roomIdsChunk) => fetchBulkChunk(path, roomIdsChunk, params)),
  );
  return results.flat();
}

export async function getPricesBulk(startDate: string, endDate: string, roomIds: string[]): Promise<any[]> {
  assertDateRangeWithinMax(startDate, endDate);
  return fetchBulk('/api/prices/bulk', roomIds, { startDate, endDate });
}

export async function getAvailabilityBulk(startDate: string, endDate: string, roomIds: string[]): Promise<any[]> {
  assertDateRangeWithinMax(startDate, endDate);
  return fetchBulk('/api/availability/bulk', roomIds, { startDate, endDate });
}

export async function getBookingsQuantityBulk(
  startDate: string, endDate: string, roomIds: string[], bookingStatus: string = 'committed',
): Promise<any[]> {
  assertDateRangeWithinMax(startDate, endDate);
  return fetchBulk('/api/bookings_quantity/bulk', roomIds, { startDate, endDate, bookingStatus });
}
